import axios from "axios";
import { useCallback, useState } from "react";

import {
  TranscriptionService,
  JobItem,
  TranscriptionResponse,
} from "../services/transcription.service";

export const useUpload = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentJob, setCurrentJob] = useState<JobItem | null>(null);

  const submitJob = async (file: File) => {
    setLoading(true);
    setError(null);
    try {
      // Get presigned URL
      const { job_id, upload_url } = await TranscriptionService.getUploadUrl();

      // Upload directly to S3
      await axios.put(upload_url, file, {
        headers: { "Content-Type": "audio/mpeg" },
      });

      // Tell the API we're done
      await TranscriptionService.submitJob(job_id, file.name);
      const jobStatus = await TranscriptionService.getJobStatus(job_id);
      setCurrentJob(jobStatus);
      return job_id;
    } catch (err) {
      setError("Upload failed, please try again.");
    } finally {
      setLoading(false);
    }
  };
  return {
    submitJob,
    loading,
    error,
    currentJob,
    reset: () => {
      setCurrentJob(null);
      setError(null);
    },
  };
};

export const useTranscription = () => {
  const [loading, setLoading] = useState(false);
  const [loadingTranscriptionError, setLoadingTranscriptionError] = useState<string | null>(null);
  const [details, setDetails] = useState<TranscriptionResponse | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const loadJobDetails = async (jobId: string) => {
    setLoading(true);
    setLoadingTranscriptionError(null);
    try {
      // Run these in parallel
      const [transcription, audioData] = await Promise.all([
        TranscriptionService.getTranscription(jobId),
        TranscriptionService.getAudioUrl(jobId),
      ]);

      setDetails(transcription);
      setAudioUrl(audioData.audio_url);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } | null } } | null)?.response
        ?.data?.detail;
      setLoadingTranscriptionError(detail ?? "Could not retrieve audio / transcription.");
    } finally {
      setLoading(false);
    }
  };

  // Stable so JobRegistry's fetch effect doesn't re-run every render
  const reset = useCallback(() => {
    setDetails(null);
    setAudioUrl(null);
    setLoadingTranscriptionError(null);
  }, []);

  return {
    loadJobDetails,
    loading,
    loadingTranscriptionError,
    details,
    audioUrl,
    reset,
  };
};
